import { useRef, useState, type RefObject } from "react";

/**
 * Remembers what had focus when a dialog opened, so focus can go back there
 * on close even when the dialog has no Radix Trigger (controlled dialogs).
 * Captured during render, before Radix moves focus into the content.
 */
export function useOpener(open: boolean): RefObject<HTMLElement | null> {
  const opener = useRef<HTMLElement | null>(null);
  const was = useRef(false);
  if (open && !was.current && typeof document !== "undefined") opener.current = document.activeElement as HTMLElement | null;
  was.current = open;
  return opener;
}

/** Same, for content components that mount when the dialog opens. */
export function useMountOpener(): HTMLElement | null {
  const [el] = useState(() => (typeof document !== "undefined" ? (document.activeElement as HTMLElement | null) : null));
  return el;
}

/** onCloseAutoFocus handler: focus the explicit target, else the opener, else let Radix decide. */
export function restoreFocus(target: HTMLElement | null | undefined) {
  return (e: Event) => {
    if (target && target.isConnected && target !== document.body) {
      e.preventDefault();
      target.focus();
    }
  };
}

/** onOpenAutoFocus handler: first form field, else the title; never the primary action. */
export function focusFirstField(e: Event) {
  const root = e.currentTarget as HTMLElement | null;
  if (!root) return;
  const field = root.querySelector<HTMLElement>("input:not([type=hidden]):not([disabled]), textarea:not([disabled]), select:not([disabled])");
  const title = root.querySelector<HTMLElement>("[data-dialog-title]");
  const target = field ?? title;
  if (target) {
    e.preventDefault();
    target.focus();
  }
}
